// ═══════════════════════════════════════════════════════════════════════════════
// Settlement detail (employer unlock + audit trail)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { USER_ROLE } from '@fdh/shared';
import type { BudgetListItem, SettlementDetailResponse, SettlementListItem } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../components/ui/ToastProvider';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { resolveApiErrorMessage } from '../../i18n/mapApiClientError';
import { settlementApi } from '../../lib/api';
import { EmployerFloatingReviewBar } from '../../components/employer/EmployerFloatingReviewBar';
import { SCROLL_BOTTOM_FOR_REVIEW_BAR } from '../../lib/layoutChrome';
import { navigateBack } from '../../lib/navigateBack';
import { MoneyReceivedDetailModal } from '../../components/settlement/MoneyReceivedDetailModal';
import { HelperResubmitSettlementBanner } from '../../components/settlement/HelperResubmitSettlementBanner';
import { PreviousHelperBanner } from '../../components/pairing/PreviousHelperBanner';
import {
  SettlementDetailTitleMenu,
  type SettlementDetailTitleMenuItem,
} from '../../components/settlement/SettlementDetailTitleMenu';
import { SettlementHistorySection } from '../../components/settlement/employer/SettlementHistorySection';
import { isHelperBudgetPeriodSettleable } from '../../utils/employerMoneyGivenSettlement';
import {
  AutoTranslationHint,
  resolveSettlementRejectedReasonDisplay,
  resolveTranslationTargetLanguageLabel,
  settlementRejectedReasonHasTranslation,
} from '../../components/translation';

type SettlementDetailLocationState = {
  from?: string;
};

/** Matches SettlementHistorySection card border and shadow (light + dark). */
const SETTLEMENT_DETAIL_CARD_CLASS =
  'rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900';

const resolveSettlementReturnPath = (
  locationState: unknown,
  fallbackPath: string,
): string => {
  if (!locationState || typeof locationState !== 'object' || !('from' in locationState)) {
    return fallbackPath;
  }
  const from = (locationState as SettlementDetailLocationState).from?.trim() ?? '';
  if (from.startsWith('/')) {
    return from;
  }
  return fallbackPath;
};

export const SettlementDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t, language } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [data, setData] = useState<SettlementDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [moneyReceivedDetailModalOpen, setMoneyReceivedDetailModalOpen] = useState(false);
  const [moneyGivenDetailModalOpen, setMoneyGivenDetailModalOpen] = useState(false);
  const [helperSettlements, setHelperSettlements] = useState<SettlementListItem[]>([]);
  const [helperSettlementsReady, setHelperSettlementsReady] = useState(false);
  const [showRejectedReasonOriginal, setShowRejectedReasonOriginal] = useState(false);

  const isEmployer = user?.role === USER_ROLE.EMPLOYER;
  const isHelper = user?.role === USER_ROLE.HELPER;

  const handleHeaderBack = () => {
    navigateBack(navigate, resolveSettlementReturnPath(location.state, '/settlements'));
  };

  const load = useCallback(async () => {
    if (!id) {
      setError(t('settlement.error'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await settlementApi.get(id, language);
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError(res.error ?? t('settlement.error'));
      }
    } catch {
      setError(t('settlement.error'));
    } finally {
      setLoading(false);
    }
  }, [id, language, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadHelperSettlements = useCallback(async () => {
    setHelperSettlementsReady(false);
    try {
      const pageSize = 100;
      const merged: SettlementListItem[] = [];
      for (let page = 1; page <= 20; page += 1) {
        const res = await settlementApi.list('all', page, pageSize);
        if (!res.success || !res.data) {
          break;
        }
        const items = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        merged.push(...items);
        if (items.length < pageSize) {
          break;
        }
      }
      setHelperSettlements(merged);
    } catch {
      setHelperSettlements([]);
    } finally {
      setHelperSettlementsReady(true);
    }
  }, []);

  useEffect(() => {
    if (!isHelper) {
      setHelperSettlementsReady(false);
      return;
    }
    void loadHelperSettlements();
  }, [id, isHelper, loadHelperSettlements]);

  const handleOpenUnlock = () => {
    setUnlockError(null);
    setUnlockOpen(true);
  };

  const handleCloseUnlock = () => {
    if (unlockLoading) return;
    setUnlockOpen(false);
  };

  const handleConfirmUnlock = async () => {
    if (!id) return;
    setUnlockLoading(true);
    setUnlockError(null);
    try {
      const res = await settlementApi.unlock(id);
      if (res.success && res.data) {
        setData(res.data);
        setUnlockOpen(false);
        showSuccessToast(t('toast.settlement.unlockSuccess'));
      } else {
        setUnlockError(res.error ?? t('settlement.error'));
      }
    } catch (err) {
      setUnlockError(resolveApiErrorMessage(err, t, 'settlement.error'));
    } finally {
      setUnlockLoading(false);
    }
  };

  const handleReviewSettlement = async () => {
    if (!id) return;
    setDecisionLoading(true);
    setDecisionError(null);
    try {
      const res = await settlementApi.review(id, { status: 'reviewed' });
      if (res.success) {
        if (res.data) {
          setData(res.data);
        }
        showSuccessToast(t('toast.settlement.reviewedSuccess'));
        navigate(resolveSettlementReturnPath(location.state, '/settlements'), { replace: true });
        return;
      }
      setDecisionError(res.error ?? t('settlement.error'));
    } catch (err) {
      setDecisionError(resolveApiErrorMessage(err, t, 'settlement.error'));
    } finally {
      setDecisionLoading(false);
    }
  };

  const handleRejectSettlement = async () => {
    if (!id) return;
    setDecisionLoading(true);
    setRejectError(null);
    try {
      const res = await settlementApi.review(id, { status: 'rejected', rejectedReason: rejectReason });
      if (res.success) {
        if (res.data) {
          setData(res.data);
        }
        setRejectOpen(false);
        setRejectReason('');
        showSuccessToast(t('toast.settlement.rejectedSuccess'));
        navigate(
          resolveSettlementReturnPath(location.state, '/settlements/pending-review'),
          { replace: true },
        );
        return;
      }
      setRejectError(res.error ?? t('settlement.error'));
    } catch (err) {
      setRejectError(resolveApiErrorMessage(err, t, 'settlement.error'));
    } finally {
      setDecisionLoading(false);
    }
  };

  const handleOpenReject = () => {
    setRejectError(null);
    setRejectReason('');
    setRejectOpen(true);
  };

  const handleCloseReject = () => {
    if (decisionLoading) return;
    setRejectOpen(false);
  };

  const handleWithdrawSettlement = async () => {
    if (!id) return;
    setDecisionLoading(true);
    setDecisionError(null);
    try {
      const res = await settlementApi.withdraw(id);
      if (res.success && res.data) {
        setData(res.data);
        setWithdrawOpen(false);
        showSuccessToast(t('toast.settlement.withdrawSuccess'));
        return;
      }
      setDecisionError(res.error ?? t('settlement.error'));
    } catch (err) {
      setDecisionError(resolveApiErrorMessage(err, t, 'settlement.error'));
    } finally {
      setDecisionLoading(false);
    }
  };

  const formatMoney = (n: number) => (n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`);

  const balanceLabel = (balance: number) => {
    if (balance >= 0) return t('settlement.surplus');
    return t('settlement.overspent');
  };

  const balanceClass = (balance: number) =>
    balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';

  const settlement = data?.settlement;
  const isFromPreviousHelper = settlement?.isFromPreviousHelper === true;
  const rejectedReason = settlement?.rejectedReason?.trim() ?? '';
  const displayRejectedReason = resolveSettlementRejectedReasonDisplay(
    rejectedReason,
    settlement?.translation,
    showRejectedReasonOriginal,
  );
  const showRejectReasonTranslation =
    isHelper &&
    settlementRejectedReasonHasTranslation(rejectedReason, settlement?.translation);
  const employerIsPaired = Boolean(user?.pairedAt && user.pairedAt.trim() !== '');
  const canEmployerReviewSettlement =
    isEmployer && employerIsPaired && settlement?.status === 'submitted' && !isFromPreviousHelper;
  const settlementStatusLabel =
    settlement?.status === 'submitted'
      ? t('settlement.status.submitted')
      : settlement?.status === 'withdrawn'
        ? t('settlement.status.withdrawn')
      : settlement?.status === 'rejected'
        ? t('settlement.status.rejected')
        : settlement?.status === 'reviewed'
          ? isEmployer
            ? t('settlement.status.settled')
            : t('settlement.locked')
          : t('settlement.unlocked');
  const settlementStatusClass =
    settlement?.status === 'submitted'
      ? 'border border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-700 dark:bg-amber-950/60 dark:text-amber-200'
      : settlement?.status === 'withdrawn'
        ? 'border border-gray-300 bg-gray-100 text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
      : settlement?.status === 'reviewed'
        ? 'border border-green-300 bg-green-100 text-green-800 dark:border-green-700 dark:bg-green-950/50 dark:text-green-200'
        : settlement?.status === 'rejected'
          ? 'border border-red-300 bg-red-100 text-red-800 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200'
          : settlement?.status === 'unlocked'
            ? 'border border-blue-300 bg-blue-100 text-blue-800 dark:border-blue-700 dark:bg-blue-950/55 dark:text-blue-200'
            : 'border border-green-300 bg-green-100 text-green-800 dark:border-green-700 dark:bg-green-950/50 dark:text-green-200';

  const settlementHistoryItems = data?.settlementHistory ?? [];

  const helperConfirmMainBudgetId = settlement?.budgetId ?? '';
  const showHelperResubmitBanner = useMemo(() => {
    if (!isHelper || !settlement || !helperConfirmMainBudgetId || !helperSettlementsReady) {
      return false;
    }
    return isHelperBudgetPeriodSettleable(
      {
        id: helperConfirmMainBudgetId,
        startDate: settlement.startDate,
        endDate: settlement.endDate,
      } as BudgetListItem,
      helperSettlements
    );
  }, [helperConfirmMainBudgetId, helperSettlements, helperSettlementsReady, isHelper, settlement]);

  const titleMenuItems = useMemo((): SettlementDetailTitleMenuItem[] => {
    if (!settlement || isFromPreviousHelper) {
      return [];
    }
    if (isEmployer) {
      if (settlement.status === 'reviewed' && settlement.locked) {
        return [
          { id: 'unlock', label: t('settlement.unlock'), onSelect: handleOpenUnlock },
          {
            id: 'money-given-detail',
            label: t('settlement.moneyGivenDetailTitle'),
            onSelect: () => setMoneyGivenDetailModalOpen(true),
          },
        ];
      }
      if (settlement.status === 'submitted') {
        return [
          {
            id: 'money-given-detail',
            label: t('settlement.moneyGivenDetailTitle'),
            onSelect: () => setMoneyGivenDetailModalOpen(true),
          },
        ];
      }
      return [];
    }
    if (isHelper) {
      const helperTerminalStatuses = new Set(['reviewed', 'rejected', 'withdrawn', 'unlocked']);
      if (helperTerminalStatuses.has(settlement.status)) {
        return [
          {
            id: 'money-received-detail',
            label: t('settlement.moneyReceivedDetailTitle'),
            onSelect: () => setMoneyReceivedDetailModalOpen(true),
          },
        ];
      }
      if (
        settlement.status === 'submitted' &&
        settlement.submittedByUserId === user?.id
      ) {
        return [
          {
            id: 'money-received-detail',
            label: t('settlement.moneyReceivedDetailTitle'),
            onSelect: () => setMoneyReceivedDetailModalOpen(true),
          },
          { id: 'withdraw', label: t('settlement.withdraw'), onSelect: () => setWithdrawOpen(true) },
        ];
      }
    }
    return [];
  }, [handleOpenUnlock, isEmployer, isFromPreviousHelper, isHelper, settlement, t, user?.id]);

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <button
                type="button"
                onClick={handleHeaderBack}
                aria-label={t('task.action.back')}
                className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4L6 10l6 6" />
                </svg>
              </button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {t('settlement.detailTitle')}
                </h1>
                {settlement ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('settlement.period', { start: settlement.startDate, end: settlement.endDate })}
                  </p>
                ) : null}
              </div>
            </div>
            <SettlementDetailTitleMenu
              ariaLabel={t('settlement.detailTitleMenuAria')}
              items={titleMenuItems}
            />
          </div>
        </div>
      </header>

      <main className={`mx-auto max-w-lg px-4 py-6 ${canEmployerReviewSettlement ? SCROLL_BOTTOM_FOR_REVIEW_BAR : 'pb-24'}`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : data && settlement ? (
          <div className="space-y-6">
            {isFromPreviousHelper ? <PreviousHelperBanner variant="settlement" /> : null}
            {showHelperResubmitBanner ? (
              <HelperResubmitSettlementBanner mainBudgetId={helperConfirmMainBudgetId} />
            ) : null}
            <div className={`${SETTLEMENT_DETAIL_CARD_CLASS} p-4`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('settlement.submittedAtLabel')}: {settlement.submittedAt}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${settlementStatusClass}`}
                >
                  {settlementStatusLabel}
                </span>
              </div>
              {settlement.status === 'rejected' && rejectedReason ? (
                <div
                  className="mt-3 rounded-xl border border-red-100 bg-red-50/95 p-4 dark:border-red-900/40 dark:bg-red-950/35"
                  role="region"
                  aria-label={t('task.today.employerFeedbackLabel')}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs font-semibold text-red-800 dark:text-red-200">
                      {t('task.today.employerFeedbackLabel')}
                    </p>
                    {showRejectReasonTranslation ? (
                      <AutoTranslationHint
                        targetLanguageLabel={resolveTranslationTargetLanguageLabel(
                          t,
                          settlement?.translation?.targetLanguage,
                          language,
                        )}
                        showingOriginal={showRejectedReasonOriginal}
                        onToggleOriginal={() => setShowRejectedReasonOriginal((prev) => !prev)}
                      />
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm text-red-900 dark:text-red-100">{displayRejectedReason}</p>
                </div>
              ) : null}
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('settlement.totalSpent')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatMoney(settlement.totalSpent)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <span>{isHelper ? t('settlement.helperStatPeriodBudget') : t('settlement.budgetAmount')}</span>
                    {data.moneyReceivedDetails.length > 0 ? (
                      <button
                        type="button"
                        onClick={() =>
                          isHelper
                            ? setMoneyReceivedDetailModalOpen(true)
                            : setMoneyGivenDetailModalOpen(true)
                        }
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
                        aria-label={
                          isHelper
                            ? t('settlement.moneyReceivedDetailOpen')
                            : t('settlement.moneyGivenDetailOpen')
                        }
                        aria-haspopup="dialog"
                        aria-expanded={
                          isHelper ? moneyReceivedDetailModalOpen : moneyGivenDetailModalOpen
                        }
                      >
                        <span aria-hidden>i</span>
                      </button>
                    ) : null}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatMoney(settlement.budgetAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('settlement.balance')}</span>
                  <span className={`font-semibold ${balanceClass(settlement.balance)}`}>
                    {balanceLabel(settlement.balance)} {formatMoney(Math.abs(settlement.balance))}
                  </span>
                </div>
              </div>
              {decisionError ? (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
                  {decisionError}
                </p>
              ) : null}
            </div>

            <section aria-labelledby="settlement-receipts-heading">
              <h2
                id="settlement-receipts-heading"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                {t('settlement.receiptsTitle')}
              </h2>
              {data.receipts.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('settlement.empty')}</p>
              ) : (
                <ul className="mt-2 space-y-2" role="list">
                  {data.receipts.map((r) => (
                    <li
                      key={r.id}
                      className={`${SETTLEMENT_DETAIL_CARD_CLASS} p-3`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2 text-sm">
                        <div>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-gray-600 dark:text-gray-300">{r.receiptDate}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {formatMoney(r.totalAmount)}
                            </span>
                          </div>
                          {r.company ? (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{r.company}</p>
                          ) : null}
                        </div>
                        {settlement.status === 'withdrawn' || settlement.status === 'rejected' ? null : (
                          <Link
                            to={`/receipts/${r.id}`}
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                            tabIndex={0}
                            aria-label={t('settlement.viewReceipt')}
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {settlementHistoryItems.length > 0 ? (
              <SettlementHistorySection
                title={t('settlement.pastSettlementHistoryTitle')}
                items={settlementHistoryItems}
                submittedAtLabel={t('settlement.submittedAtLabel')}
                eventAtLabel={t('settlement.historyEventAtLabel')}
                moneyGivenAmountLabel={t('settlement.budgetAmount')}
                totalSpentLabel={t('settlement.totalSpent')}
                settlementResultLabel={t('settlement.balance')}
                rejectReasonLabel={t('task.today.employerFeedbackLabel')}
                receiptsTitle={t('settlement.receiptsTitle')}
                viewDetailAria={t('settlement.historyViewDetailAria')}
                closeLabel={t('common.close')}
              />
            ) : null}

            {!isEmployer && data.unlockHistory.length > 0 ? (
              <section aria-labelledby="settlement-audit-heading">
                <h2
                  id="settlement-audit-heading"
                  className="text-base font-semibold text-gray-900 dark:text-gray-100"
                >
                  {t('settlement.auditTitle')}
                </h2>
                <ul className="mt-2 space-y-2" role="list">
                  {data.unlockHistory.map((h, idx) => (
                    <li
                      key={`${h.unlockedAt}-${idx}`}
                      className={`${SETTLEMENT_DETAIL_CARD_CLASS} p-3 text-sm`}
                    >
                      <p className="text-gray-900 dark:text-gray-100">{h.unlockedAt}</p>
                      <p className="text-gray-500 dark:text-gray-400">
                        {h.unlockedByNickname || h.unlockedByUserId}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        ) : null}
      </main>

      {canEmployerReviewSettlement ? (
        <EmployerFloatingReviewBar
          onReject={handleOpenReject}
          onApprove={() => void handleReviewSettlement()}
          disabled={decisionLoading}
          rejectAriaLabel={t('task.review.reject')}
          approveAriaLabel={t('settlement.reviewedAction')}
        />
      ) : null}

      <ConfirmDialog
        open={unlockOpen}
        title={t('settlement.unlockConfirmTitle')}
        description={t('settlement.unlockConfirmBody')}
        confirmLabel={unlockLoading ? t('settlement.unlocking') : t('settlement.unlock')}
        cancelLabel={t('task.action.back')}
        onConfirm={() => void handleConfirmUnlock()}
        onCancel={handleCloseUnlock}
        confirmDisabled={unlockLoading}
        cancelDisabled={unlockLoading}
      >
        {unlockError ? (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
            {unlockError}
          </p>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={rejectOpen}
        title={t('settlement.rejectModalTitle')}
        description={t('settlement.rejectReasonPrompt')}
        confirmLabel={decisionLoading ? t('common.loading') : t('task.review.reject')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleRejectSettlement()}
        onCancel={handleCloseReject}
        variant="danger"
        confirmDisabled={decisionLoading}
        cancelDisabled={decisionLoading}
      >
        <textarea
          value={rejectReason}
          onChange={(event) => setRejectReason(event.target.value)}
          rows={4}
          className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          aria-label={t('settlement.rejectReasonPrompt')}
        />
        {rejectError ? (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
            {rejectError}
          </p>
        ) : null}
      </ConfirmDialog>

      {moneyReceivedDetailModalOpen && data && settlement ? (
        <MoneyReceivedDetailModal
          open
          onClose={() => setMoneyReceivedDetailModalOpen(false)}
          settlementStart={settlement.startDate}
          settlementEnd={settlement.endDate}
          rows={data.moneyReceivedDetails}
          t={t}
          titleKey="settlement.moneyReceivedDetailTitle"
          titleId="settlement-detail-money-received-title"
        />
      ) : null}
      {moneyGivenDetailModalOpen && data && settlement ? (
        <MoneyReceivedDetailModal
          open
          onClose={() => setMoneyGivenDetailModalOpen(false)}
          settlementStart={settlement.startDate}
          settlementEnd={settlement.endDate}
          rows={data.moneyReceivedDetails}
          t={t}
          titleKey="settlement.moneyGivenDetailTitle"
          titleId="settlement-detail-money-given-title"
        />
      ) : null}

      <ConfirmDialog
        open={withdrawOpen}
        title={t('toast.settlement.withdrawConfirmTitle')}
        description={t('toast.settlement.withdrawConfirmBody')}
        confirmLabel={decisionLoading ? t('common.loading') : t('settlement.withdraw')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleWithdrawSettlement()}
        onCancel={() => {
          if (decisionLoading) {
            return;
          }
          setWithdrawOpen(false);
        }}
        confirmDisabled={decisionLoading}
        cancelDisabled={decisionLoading}
      />
    </div>
  );
};
